'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { generateRandomString, generateCodeChallenge } from '@/lib/pkce';

export interface OidcUser {
	id: string;
	email?: string;
	fullName?: string;
	firstName?: string;
	lastName?: string;
	imageUrl?: string;
	createdAt?: Date;
	primaryEmailAddress?: {
		emailAddress: string;
	};
}

interface AuthContextType {
	isSignedIn: boolean;
	isLoaded: boolean;
	userId: string | null;
	user: OidcUser | null;
	getToken: () => Promise<string | null>;
	signOut: () => void;
	openSignIn: (opts?: any) => void;
}

const AuthContext = createContext<AuthContextType>({
	isSignedIn: false,
	isLoaded: false,
	userId: null,
	user: null,
	getToken: async () => null,
	signOut: () => {},
	openSignIn: () => {},
});

function parseJwt(token: string) {
	try {
		const parts = token.split('.');
		if (!parts[1]) return null;
		const base64Url = parts[1];
		const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split('')
				.map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
				.join(''),
		);
		return JSON.parse(jsonPayload);
	} catch {
		try {
			const parts = token.split('.');
			if (!parts[1]) return null;
			return JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
		} catch {
			return null;
		}
	}
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const [token, setToken] = useState<string | null>(null);
	const [user, setUser] = useState<OidcUser | null>(null);
	const [isLoaded, setIsLoaded] = useState<boolean>(false);

	useEffect(() => {
		// Try to read token from localStorage or cookie
		let savedToken = localStorage.getItem('qrcodly_oidc_token');
		if (!savedToken) {
			const match = document.cookie.match(/(?:^|; )qrcodly_oidc_token=([^;]*)/);
			if (match && match[1]) {
				savedToken = decodeURIComponent(match[1]);
			}
		}

		if (savedToken) {
			const claims = parseJwt(savedToken);
			if (claims && claims.exp && claims.exp * 1000 > Date.now()) {
				setToken(savedToken);
				const userId = claims.sub || claims.sid || 'user_oidc';
				const email = claims.email || claims.preferred_username || '';
				const fullName = claims.name || claims.preferred_username || email.split('@')[0] || 'User';
				const firstName = claims.given_name || fullName.split(' ')[0];
				const lastName = claims.family_name || fullName.split(' ').slice(1).join(' ');

				setUser({
					id: userId,
					email,
					fullName,
					firstName,
					lastName,
					primaryEmailAddress: {
						emailAddress: email,
					},
					imageUrl: claims.picture || undefined,
				});
			} else {
				localStorage.removeItem('qrcodly_oidc_token');
				document.cookie = 'qrcodly_oidc_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
			}
		}
		setIsLoaded(true);
	}, []);

	const getToken = useCallback(async () => {
		return token;
	}, [token]);

	const signOut = useCallback(() => {
		localStorage.removeItem('qrcodly_oidc_token');
		document.cookie = 'qrcodly_oidc_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
		setToken(null);
		setUser(null);
		const oidcIssuer = process.env.NEXT_PUBLIC_OIDC_ISSUER_URL || 'https://keycloak.dslan.at/realms/dslan';
		const frontendUrl = process.env.NEXT_PUBLIC_FRONTEND_URL || 'https://qr.dslan.at';
		window.location.href = `${oidcIssuer}/protocol/openid-connect/logout?post_logout_redirect_uri=${encodeURIComponent(frontendUrl)}`;
	}, []);

	const openSignIn = useCallback(async () => {
		const oidcIssuer = process.env.NEXT_PUBLIC_OIDC_ISSUER_URL || 'https://keycloak.dslan.at/realms/dslan';
		const oidcClientId = process.env.NEXT_PUBLIC_OIDC_CLIENT_ID || 'qrcodly';
		const frontendUrl = process.env.NEXT_PUBLIC_FRONTEND_URL || 'https://qr.dslan.at';
		const verifier = generateRandomString(64);
		const challenge = await generateCodeChallenge(verifier);
		document.cookie = `qrcodly_pkce_verifier=${verifier}; path=/; max-age=600; SameSite=Lax`;
		window.location.href = `${oidcIssuer}/protocol/openid-connect/auth?client_id=${oidcClientId}&redirect_uri=${encodeURIComponent(`${frontendUrl}/api/auth/callback/oidc`)}&response_type=code&scope=openid%20profile%20email&code_challenge=${challenge}&code_challenge_method=S256`;
	}, []);

	const value: AuthContextType = {
		isSignedIn: Boolean(token && user),
		isLoaded,
		userId: user?.id || null,
		user,
		getToken,
		signOut,
		openSignIn,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
	const context = useContext(AuthContext);
	return {
		isSignedIn: context.isSignedIn,
		isLoaded: context.isLoaded,
		userId: context.userId,
		getToken: context.getToken,
		signOut: context.signOut,
	};
}

export function useUser() {
	const context = useContext(AuthContext);
	return {
		isSignedIn: context.isSignedIn,
		isLoaded: context.isLoaded,
		user: context.user,
	};
}

export function useClerk() {
	const context = useContext(AuthContext);
	return {
		openSignIn: context.openSignIn,
		signOut: context.signOut,
	};
}

export function useSession() {
	const context = useContext(AuthContext);
	return {
		isLoaded: context.isLoaded,
		session: context.isSignedIn ? { user: context.user } : null,
	};
}
